import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

/**
 * Editorial form input. Hairline border, vellum-dim fill, eyebrow-style label
 * above the field, focus shifts the border to violet, errors render in italic
 * violet-deep with an em-dash prefix (NEVER red — would shatter the palette).
 */
export const AuthInput = forwardRef<HTMLInputElement, Props>(function AuthInput(
  { label, error, hint, ...rest },
  ref,
) {
  const reactId = useId();
  const id = rest.name ? `auth-${rest.name}` : reactId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  return (
    <div className="auth-field">
      <label htmlFor={id} className="auth-label">
        {label}
      </label>
      <input
        {...rest}
        id={id}
        ref={ref}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        className="auth-input"
      />
      {error && (
        <p id={errorId} className="auth-error">
          — {error}
        </p>
      )}
      {!error && hint && (
        <p id={hintId} className="auth-hint">
          {hint}
        </p>
      )}
    </div>
  );
});
